/**
 * Panel date/time display — Asia/Tashkent wall clock as {@code HH:mm dd.MM.yyyy}.
 *
 * Report dates without a time part are formatted as {@code dd.MM.yyyy} only.
 */

const DISPLAY_TZ = 'Asia/Tashkent'

function parts(iso: string): Intl.DateTimeFormatPart[] {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: DISPLAY_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
}

function part(list: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): string {
  return list.find((p) => p.type === type)?.value ?? ''
}

/** Date-only values (`yyyy-MM-dd`) → {@code dd.MM.yyyy}. */
export function formatReportDate(value: string | null | undefined): string {
  if (!value) {
    return '—'
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-')
    return `${d}.${m}.${y}`
  }
  const p = parts(value)
  return `${part(p, 'day')}.${part(p, 'month')}.${part(p, 'year')}`
}

/** Instant / offset datetime → {@code HH:mm dd.MM.yyyy}. */
export function formatDateTime(value: string | null | undefined): string {
  if (!value) {
    return '—'
  }
  const p = parts(value)
  return `${part(p, 'hour')}:${part(p, 'minute')} ${part(p, 'day')}.${part(p, 'month')}.${part(p, 'year')}`
}

/** Tashkent calendar day of an instant, as yyyy-MM-dd. */
export function tashkentDay(value: string | Date): string {
  const p = parts(typeof value === 'string' ? value : value.toISOString())
  return `${part(p, 'year')}-${part(p, 'month')}-${part(p, 'day')}`
}

/**
 * Whole Tashkent calendar days from today to {@code value}: 0 today, negative once
 * past (web-app DebtBlockModal counted midnight to midnight).
 */
export function daysUntil(value: string, now: Date = new Date()): number {
  const day = (iso: string) => Date.parse(`${iso}T00:00:00Z`)
  return Math.round((day(tashkentDay(value)) - day(tashkentDay(now))) / 86_400_000)
}

/** A Tashkent calendar day (yyyy-MM-dd) as the instant of its start. */
export function tashkentStartOfDay(day: string): string {
  return new Date(`${day}T00:00:00+05:00`).toISOString()
}

/** A Tashkent calendar day (yyyy-MM-dd) as the instant of its last second (contract end dates). */
export function tashkentEndOfDay(day: string): string {
  return new Date(`${day}T23:59:59+05:00`).toISOString()
}

/** {@code days} calendar days after a Tashkent day (yyyy-MM-dd). */
export function addDays(day: string, days: number): string {
  const date = new Date(`${day}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return date.toISOString().slice(0, 10)
}

/** {@code months} calendar months after a Tashkent day, clamped to the month's last day. */
export function addMonths(day: string, months: number): string {
  const [y, m, d] = day.split('-').map(Number)
  const target = new Date(Date.UTC(y, m - 1 + months, 1))
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate()
  target.setUTCDate(Math.min(d, last))
  return target.toISOString().slice(0, 10)
}
